import { CancelablePromise, makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import { ButtonBox, Flex, Loader, OutsideClick, Text } from '@upsales/components';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import FileStorageApps from 'Components/FileStorageApps';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FoldersResource from 'Resources/FileFolder';
import Client from 'App/resources/Model/Client';
import T from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';
import FolderModal from './FolderModal';
import logError from 'Helpers/logError';
import { DndProvider } from 'react-dnd';
import GridView from './GridView';
import ListView from './ListView';
import Header from './Header';

import './AccountFiles.scss';

export type FolderType = {
	id: number;
	name: string;
	folderId: number | null;
};

export type FolderTreeType = {
	id: number;
	name: string;
};

export type FileType = {
	client: { id: number };
	id: number;
	name: string;
	size: number;
	extension: string;
	date: string;
	userId: number;
	folderId: number | null;
	relatedId?: number;
	related?: string;
	userRemovable: boolean;
};

export type FolderWithCount = {
	id: number;
	name: string;
	folderId: number | null;
	directChildFolders: FolderWithCount[];
	childFiles: FileType[];
	totalSubFolderCount: number;
};

const useFolderTree = (folders: FolderType[], files: FileType[]) => {
	return useMemo(() => {
		const folderMap = new Map<number, FolderWithCount>();

		folders.forEach(folder => {
			folderMap.set(folder.id, {
				...folder,
				directChildFolders: [],
				childFiles: [],
				totalSubFolderCount: 0
			});
		});

		folders.forEach(folder => {
			const currentFolder = folderMap.get(folder.id);
			if (!currentFolder) return;

			if (folder.folderId !== null) {
				const parentFolder = folderMap.get(folder.folderId);
				if (parentFolder) {
					parentFolder.directChildFolders.push(currentFolder);
				}
			}
		});

		files.forEach(file => {
			if (file.folderId !== null) {
				const folder = folderMap.get(file.folderId);
				if (folder) {
					folder.childFiles.push(file);
				}
			}
		});

		const collectAllFiles = (folder: FolderWithCount, visited = new Set<number>()): FileType[] => {
			if (visited.has(folder.id)) return [];
			visited.add(folder.id);

			const directFiles = folder.childFiles;

			const childResults = folder.directChildFolders.map(child => collectAllFiles(child, visited));
			const childFiles = childResults.flatMap(result => result);

			const allFiles = [...directFiles, ...childFiles];

			return allFiles;
		};

		const countAllSubFolders = (folder: FolderWithCount, visited = new Set<number>()): number => {
			if (visited.has(folder.id)) return 0;
			visited.add(folder.id);

			const directFolderCount = folder.directChildFolders.length;
			const childCounts = folder.directChildFolders.map(child => countAllSubFolders(child, visited));
			const totalChildCount = childCounts.reduce((sum, count) => sum + count, 0);

			return directFolderCount + totalChildCount;
		};

		folderMap.forEach(folder => {
			folder.childFiles = collectAllFiles(folder);
			folder.totalSubFolderCount = countAllSubFolders(folder);
		});
		return {
			folderMap
		};
	}, [folders, files]);
};

const AccountFiles = ({ client }: { client: Client }) => {
	const classes = new BemClass('AccountFiles');

	const promises = useRef<CancelablePromise[]>([]);

	const [allFolders, setAllFolders] = useState<FolderType[]>([]);
	const [allFiles, setAllFiles] = useState<FileType[]>([]);
	const [folderTree, setFolderTree] = useState<FolderTreeType[]>([]);
	const [listMode, setListMode] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [showAddFolderModal, setShowAddFolderModal] = useState(false);
	const [searchString, setSearchString] = useState<string | null>(null);

	const currentFolderId = folderTree[folderTree.length - 1]?.id ?? null;

	const getFoldersToShow = () => {
		if (searchString !== null) {
			return allFolders.filter(folder => folder.name.toLowerCase().includes(searchString));
		}
		return allFolders.filter(folder => folder.folderId === currentFolderId);
	};
	const foldersToShow = getFoldersToShow();

	const getFilesToShow = () => {
		if (searchString !== null) {
			return allFiles.filter(file => file.name.toLowerCase().includes(searchString));
		}
		return allFiles.filter(file => file.folderId === currentFolderId);
	};

	const filesToShow = getFilesToShow();

	const { folderMap } = useFolderTree(allFolders, allFiles);

	const fetchAllFilesAndFolders = async () => {
		if (!client.id) {
			return;
		}
		setIsLoading(true);

		const promise = makeCancelable(FoldersResource.getFilesAndFolders(client.id));
		promises.current.push(promise);
		promise.promise
			.then(res => {
				setAllFolders(res.folders);
				setAllFiles(res.files);
			})
			.catch(e => logError(e, 'Could not fetch files and folders'))
			.finally(() => setIsLoading(false));
	};

	useEffect(() => {
		fetchAllFilesAndFolders();
		if (localStorage.getItem('boxViewDocuments') === '1') {
			setListMode(false);
		}

		return () => {
			promises.current.forEach(p => p.cancel());
		};
	}, []);

	// This might not be that good?
	useEffect(() => {
		const listener = Tools.$rootScope.$on('file.uploaded', (e, file) => {
			file.name = file.filename;
			setAllFiles(currentFiles => [...currentFiles, { ...file, folderId: currentFolderId }]);
		});
		return () => listener();
	}, [currentFolderId]);

	const buildFolderTree = (folderId: number) => {
		const tree = [];
		let id: number | null = folderId;
		while (id) {
			const parentFolder = allFolders.find(folder => folder.id === id);
			if (parentFolder) {
				tree.unshift({ id: parentFolder.id, name: parentFolder.name });
				id = parentFolder?.folderId;
			} else {
				break;
			}
		}
		setFolderTree(tree);
		setSearchString(null);
	};

	const openFolder = async (folderId: number, name: string) => {
		if (searchString !== null && folderId) {
			buildFolderTree(folderId);
			return;
		}
		setFolderTree(currentTree => [...currentTree, { id: folderId, name }]);
	};

	const changeFolderFromTree = async (folderId?: number | null) => {
		if (folderId === currentFolderId) return;

		setFolderTree(currentTree => {
			const indexOfFolder = currentTree.findIndex(folder => folder.id === folderId);
			const newfolderTree = [...currentTree];
			newfolderTree.length = indexOfFolder + 1;
			return newfolderTree;
		});
	};

	const createFolder = (name: string) => {
		if (!client.id) {
			return;
		}
		const promise = makeCancelable(FoldersResource.save({ name, clientId: client.id, folderId: currentFolderId }));
		promises.current.push(promise);
		promise.promise
			.then(res => {
				setAllFolders(currentFolders => [...currentFolders, { ...res.data, folderId: currentFolderId }]);
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderCreated'
				});
			})
			.catch(e => {
				logError(e, 'Could not create folder');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderCreateFailed'
				});
			});
	};

	const changeFolderFolder = (id: number, folderId: number | null) => {
		if (!client.id) {
			return;
		}
		const promise = makeCancelable(FoldersResource.save({ clientId: client.id, id, folderId }));
		promises.current.push(promise);
		promise.promise
			.then(() => {
				const newFolders = [...allFolders].map(folder => {
					if (folder.id === id) {
						folder.folderId = folderId;
					}
					return folder;
				});
				setAllFolders(newFolders);
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderMoved'
				});
			})
			.catch(e => {
				logError(e, 'Could not move folder');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderMoveFailed'
				});
			});
	};

	const changeFileFolder = (id: number, folderId: number | null) => {
		const promise = makeCancelable(FoldersResource.changeFileFolder(id, folderId));
		promises.current.push(promise);

		promise.promise
			.then(() => {
				const newFiles = [...allFiles].map(file => {
					if (file.id === id) {
						file.folderId = folderId;
					}
					return file;
				});
				setAllFiles(newFiles);
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'file.fileMoved'
				});
			})
			.catch(e => {
				logError(e, 'Could not move file');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'file.fileMoveFailed'
				});
			});
	};

	const addFile = () => {
		if (!client.id) {
			return;
		}
		if (Tools.FeatureHelper.hasSoftDeployAccess('REACT_UPLOAD_FILE_MODAL')) {
			return openModal('UploadFileModal', {
				accountId: client.id,
				folderId: currentFolderId,
				closeOnUpload: true
			});
		}
		Tools.$upModal
			.open('uploadFile', {
				accountId: client.id,
				folderId: currentFolderId,
				closeOnUpload: true
			})
			.catch(e => logError(e, 'Could not upload file'));
	};

	const deleteFolder = async (id: number, totalFiles: number) => {
		if (totalFiles > 0) {
			const canDelete = folderMap.get(id)?.childFiles.every(file => file.userRemovable);
			if (!canDelete) {
				const errorMsg = 'noDeleteRights.documents';
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: errorMsg
				});
				return;
			}
		}
		const promise = makeCancelable(FoldersResource.delete(id));
		promises.current.push(promise);

		return promise.promise
			.then(() => {
				setAllFolders(currentFolders => {
					const newFolders = [...currentFolders];
					const index = currentFolders.findIndex(folder => folder.id === id);
					newFolders.splice(index, 1);
					return newFolders;
				});
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderDeleted'
				});
			})
			.catch(err => {
				if (err.response.status === 400) {
					Tools.NotificationService.addNotification({
						style: Tools.NotificationService.style.ERROR,
						icon: 'times',
						title: 'default.error',
						body: 'foler.folderNotEmpty'
					});
				} else {
					logError(err, 'Could not delete folder');
				}
			});
	};

	const deleteFile = (file: FileType) => {
		const promise = makeCancelable(Tools.File.delete(file, { noConfirm: true }));
		promises.current.push(promise);
		promise.promise
			.then(res => {
				if (!res) {
					return;
				}
				setAllFiles(currentFiles => {
					const newFiles = [...currentFiles];
					const index = currentFiles.findIndex(f => f.id === file.id);
					newFiles.splice(index, 1);
					return newFiles;
				});
			})
			.catch(e => {
				logError(e, 'Could not delete file');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'deleteError.file'
				});
			});
	};

	const changeNameOfFolder = (id: number, name: string) => {
		if (!client.id) {
			return;
		}
		const promise = makeCancelable(FoldersResource.save({ name, clientId: client.id, id }));
		promises.current.push(promise);
		promise.promise
			.then(() => {
				setFolderTree(currentFolderTree =>
					currentFolderTree.map(folder => {
						if (folder.id === id) {
							folder.name = name;
						}
						return folder;
					})
				);
				setAllFolders(currentFolders =>
					currentFolders.map(folder => {
						if (folder.id === id) {
							folder.name = name;
						}
						return folder;
					})
				);
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderNameChanged'
				});
			})
			.catch(e => {
				logError(e, 'Could not change name');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderNameChangeFailed'
				});
			});
	};

	const content = () => {
		let element;
		if (!filesToShow.length && !foldersToShow.length) {
			if (searchString) {
				element = <Text size="lg">{T('noResult.generic')}</Text>;
			} else if (showAddFolderModal) {
				element = (
					<OutsideClick outsideClick={() => setShowAddFolderModal(false)} targetClass={'FolderModal'}>
						<div>
							<FolderModal
								animate
								noDropDown
								onClick={name => {
									createFolder(name);
									setShowAddFolderModal(false);
								}}
							/>
						</div>
					</OutsideClick>
				);
			} else {
				element = (
					<>
						<ButtonBox selectable={false} onClick={addFile} icon="file" title={T('file.addFile')} />
						<ButtonBox
							selectable={false}
							onClick={() => setShowAddFolderModal(true)}
							icon="folder"
							title={T('file.addFolder')}
						/>
					</>
				);
			}

			return (
				<Flex className={classes.elem('center').b()} gap="u10" justifyContent="center" alignItems="center">
					{element}
				</Flex>
			);
		}

		const filesViewProps = {
			folderMap,
			folders: foldersToShow,
			files: filesToShow,
			openFolder,
			deleteFolder,
			deleteFile,
			changeFolderFolder,
			changeFileFolder,
			changeNameOfFolder,
			clientId: client.id
		};

		if (listMode) {
			return <ListView {...filesViewProps} />;
		}
		return <GridView {...filesViewProps} />;
	};

	if (isLoading) {
		return (
			<Flex className={classes.elem('center-loader').b()} justifyContent="center" alignItems="center">
				<Loader size="xl" />
			</Flex>
		);
	}
	return (
		<div className={classes.b()}>
			<DndProvider backend={HTML5Backend}>
				<Header
					folders={folderTree}
					addFolder={createFolder}
					addFile={addFile}
					changeFolder={changeFolderFromTree}
					setSearchString={setSearchString}
					searchString={searchString}
					setListMode={setListMode}
					listMode={listMode}
					changeFolderFolder={changeFolderFolder}
					changeFileFolder={changeFileFolder}
					numberOfFolders={foldersToShow.length}
					numberOfFiles={filesToShow.length}
				/>
				{content()}
				{Tools.AppService.getFileStorageIntegrations().length > 0 ? (
					<div className={classes.elem('google-drive-container').b()}>
						<FileStorageApps
							apps={Tools.AppService.getFileStorageIntegrations()}
							objectId={client.id}
							type="client"
							displayTypeFromParent={listMode ? 'list' : 'grid'}
						/>
					</div>
				) : null}
			</DndProvider>
		</div>
	);
};

export default AccountFiles;
